import type { FC } from "react";
import type React from "react";
import { useRef } from "react";
import styled from "styled-components";
import { Textfield } from "@similarweb/ui-components";
import type { IDynamicFilterRule, IItem } from "@similarweb/pro-logic-components";
import { getParsedKeywords } from "./utils";
import type { multiIncludeExcludeType } from "./consts";
import { MAX_KEYWORDS_PER_RULE, TEXT_BOX_WIDTH } from "./consts";
import { translate } from "app/filters/i18nFilter";

const ENTER = "Enter";
const NAME = "name";
export interface DefaultInputFieldProps {
    rule: IDynamicFilterRule;
    maxItemsPerRule?: number;
    textBoxWidth?: number;
    applyButtonText?: string;
    onAddNewRule: (newValues: IItem[]) => void;
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType];
    placeholder?: string;
}

const RuleTextFiled = styled(Textfield)<{ width: number }>`
    width: ${({ width }) => `${width}px`};
    .input-container {
        padding-right: 0;
        input {
            padding: 0;
        }
    }
`;

export const FilterInputField: FC<DefaultInputFieldProps> = ({
    rule,
    maxItemsPerRule = MAX_KEYWORDS_PER_RULE,
    textBoxWidth = TEXT_BOX_WIDTH,
    applyButtonText,
    onAddNewRule,
    filterType,
    placeholder = translate(`multi.include.exclude.${filterType}.placeholder`),
}) => {
    const inputRef = useRef(null);
    const didExceedMaxItems = (filterRule: IDynamicFilterRule) => filterRule.items?.length >= maxItemsPerRule;

    const addNewRule = (e) => {
        const selectedValue = e.target.value ?? inputRef.current?.getValue?.();
        const newValue: IItem[] = getParsedKeywords(
            Object.prototype.hasOwnProperty.call(selectedValue, NAME) ? selectedValue.name : selectedValue,
            filterType,
        );
        onAddNewRule(newValue);
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ENTER) {
            addNewRule(e);
            e.currentTarget.value = "";
            if (rule.items.length === maxItemsPerRule - 1) {
                e.currentTarget.blur();
            }
        }
    };

    const isDisabled = didExceedMaxItems(rule);
    return (
        <RuleTextFiled
            ref={inputRef}
            onApplyClick={addNewRule}
            onKeyUp={onKeyUp}
            disabled={isDisabled}
            ApplyButtonText={applyButtonText}
            placeholder={placeholder}
            width={textBoxWidth}
            isFocused={true}
            inputFocusParameters={{ preventScroll: true }}
            iconName="globe"
        />
    );
};
